import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ApiParamConstants, ApiUrlConstants } from '../../shared/utils/api-constants';
import { FacilityCreate, FacilityEdit, FacilityView } from '../../shared/models/facility';
import { ListHttpParameters } from '../../shared/models/list-http-parameters';
import { ListView } from '../../shared/models/list-view';
import { FacilityCreationSuccessResponse } from '../../shared/models/facility-creation-success-response';
import { UrlConstants } from '../../shared/utils/url-constants';
import { StatusUpdateRequest } from '../../shared/models/status-update-request';
import { AppConfigService } from './app-config.service';
import { AppHttpParams } from '../../shared/models/app-http-params';
import { FacilityTypeEnum } from '../../shared/enums/facility-type-enum';
import { KeyWordTranslatorService } from './key-word-translator.service';
import { TranslateService } from '@ngx-translate/core';
import { EntityStatusEnum } from '../../shared/enums/entity-status.enum';
import { AuthService } from './auth.service';
import { AppConstants } from '../../shared/utils/app-constants';
import { FacilityWithUidCount } from 'src/app/shared/models/facilityWithUidCount';
import { FacilityWithProductionVolume } from 'src/app/shared/models/facility-with-production-volume';
import { map, tap, catchError } from 'rxjs/operators';
import { MOCK_FACILITY_DATA } from 'src/app/shared/models/facility-MockData';

@Injectable({
  providedIn: 'root'
})
export class FacilitiesService {

  constructor(private http: HttpClient,
    private authService: AuthService,
    private keyWordTranslatorService: KeyWordTranslatorService,
    private translateService: TranslateService) {
  }

  create(facility: FacilityCreate): Observable<FacilityCreationSuccessResponse> {
    const appParams = new AppHttpParams({ responseLabel: 'facilities.newFacility', notifySuccess: true });
    return this.http.post<FacilityCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.FACILITIES, facility, { params: appParams });
  }

  update(facilityId: string, facility: FacilityEdit): Observable<FacilityCreationSuccessResponse> {
    const appParams = new AppHttpParams({ responseLabel: 'facilities.editFacility', notifySuccess: true });
    return this.http.patch<FacilityCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.FACILITIES + UrlConstants.SEPARATOR + facilityId, facility, { params: appParams });
  }

  getListView(params: ListHttpParameters): Observable<ListView<FacilityView>> {
     const originalStatus = params.status;
     const originalActivated = params.activated;
    if (params.objectName && !params.objectId) {
      return of(null);
    }

    if (params.keyword) {
      params = this.keyWordTranslatorService.translateCountries(params, ApiParamConstants.LIST_COUNTRIES);
      params = this.keyWordTranslatorService.translateEnumValues(params, ApiParamConstants.LIST_TYPE, FacilityTypeEnum);
      params = this.keyWordTranslatorService.translateEnumValues(params, ApiParamConstants.LIST_ACTIVATED, EntityStatusEnum);
    }
    const userRole = JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_ROLE));
    params.status = originalStatus;
    if (originalActivated !== null && params.activated === null) {
      params.activated = originalActivated;
    }
    
    const httpParams = params.getHttpParams();
    if (!httpParams[ApiParamConstants.LIST_SORT]) {
      httpParams[ApiParamConstants.LIST_SORT] = 'UPDATEDATE_DESC';
    }

    const appParams = new AppHttpParams({ params: httpParams, notifyError: true })

    return this.http.get<ListView<FacilityView>>(AppConfigService.eoApiUrl + ApiUrlConstants.FACILITIES, { params: appParams, headers: { 'X-User-Role': userRole } });
  }

  getOne(facilityId: string): Observable<FacilityView> {
    const appParams = new AppHttpParams({ responseLabel: 'facilities.viewFacility', notifyError: true });
    return this.http.get<FacilityView>(AppConfigService.eoApiUrl + ApiUrlConstants.FACILITIES + UrlConstants.SEPARATOR + facilityId, { params: appParams });
  }

  updateStatus(facilityId: string, statusUpdateRequest: StatusUpdateRequest): Observable<FacilityCreationSuccessResponse> {
    const userRole = JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_ROLE));
    const appParams = new AppHttpParams({ responseLabel: 'facilities.updateStatus', notifyError: true, notifySuccess: true });
    return this.http.patch<FacilityCreationSuccessResponse>(AppConfigService.eoApiUrl + ApiUrlConstants.FACILITIES
      + UrlConstants.SEPARATOR + facilityId + ApiUrlConstants.FACILITIES_STATUS, statusUpdateRequest, { params: appParams, headers: { 'X-User-Role': userRole } });
  }

  /**
 * Gets facilities with their UID production counts
 * @param params Optional list parameters
 * @returns Observable of facilities with production volumes
 */
  getFacilitiesWithUidCounts(params?: ListHttpParameters): Observable<FacilityWithProductionVolume[]> {
    let httpParams = params ? params.getHttpParams() : {};
    const appParams = new AppHttpParams({ params: httpParams, notifyError: true });
    
    return this.http.get<FacilityWithUidCount[]>(
      AppConfigService.eoApiUrl + ApiUrlConstants.DASHBOARD_METRICS + ApiUrlConstants.FACILITY_UIDS,
      { params: appParams }
    ).pipe(
      map(response => {
        return response.map(item => ({
          name: item.facility.EO_Name1,
          companyId: item.facility.EO_ID,
          facilityId: item.facility.F_ID,
          factory: item.facility.F_Name,
          ytdProductionVolume: item.count || 0,
          domainIds: item.domainIds
        }));
      })
    );
  }

  /**
 * Checks if a facility name is available for a specific Economic Operator
 * @param name The facility name to check
 * @param eoId The economic operator ID
 * @returns Observable with availability status
 */
  checkFacilityNameAvailability(name: string, eoId: string): Observable<{ available: boolean }> {
    const appParams = new AppHttpParams({ notifyError: false });
    return this.http.get<{ available: boolean }>(
      AppConfigService.eoApiUrl + ApiUrlConstants.FACILITIES + '/checkNameAvailability',
      {
        params: appParams.appendAll({
          name: name,
          eoId: eoId
        })
      }
    );
  }

  getFacilitySuggestions(keyword: string): Observable<{ [key: string]: string[] }> {
    if (!keyword?.trim()) {
      return of({});
    }
  
    return this.http.get<{ [key: string]: string[] }>(
      `${AppConfigService.eoApiUrl}/facilities/suggestions`,
      { params: new HttpParams().set('keyword', keyword).set('limit', '5') }
    ).pipe(
      catchError(() => of({}))
    );
  }

  getFacilityLookup(domainIds: string, eoIds?: string, statuses?: string): Observable<any[]> {
    const url = `${AppConfigService.eoApiUrl}${AppConfigService.settings.apiServer.apiVersion}/facilities/lookup`;
    let appParams = new AppHttpParams({ notifyError: true });
    
    if (domainIds) {
      appParams = appParams.set('domainId', domainIds) as AppHttpParams;
    }
    
    if (eoIds) {
      appParams = appParams.set('eoId', eoIds) as AppHttpParams;
    }

    if (statuses) {
      appParams = appParams.set('status', statuses) as AppHttpParams;
    }
    
    return this.http.get<any[]>(url, { params: appParams });
  }
}
